import { Button, Modal, Stack, Text, Group, LoadingOverlay } from "@mantine/core";
import { useForm } from "@mantine/form";
import type { FC } from "react";
import { get } from "es-toolkit/compat";
import { useCallback, useMemo, useState } from "react";
import type { BillingAddress, TaxInfo } from "@project/common";
import { AddressForm } from "../../../../components/AddressForm";
import { ErrorMessage } from "../../../../components/ErrorMessage";
import { FormattedMessage, useIntl } from "react-intl";
import { billingAddressSchemaCustom } from "../../../../utils/schema";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "../../../../trpc";
import type { StripeError } from "@stripe/stripe-js";
import type { TRPCClientErrorLike } from "@trpc/client";
import type { RootRouter } from "@project/api";
import type { Account } from "../../../admin/Accounts";

export interface AddressModalProps {
  opened: boolean;
  account: Account;
  onClose: () => void;
}

export const AddressModal: FC<AddressModalProps> = ({ opened, onClose, account }) => {
  const intl = useIntl();
  const elements = useElements();
  const stripe = useStripe();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [submitError, setSubmitError] = useState<TRPCClientErrorLike<RootRouter> | StripeError | null>(null);

  const validate = useMemo(() => zod4Resolver(billingAddressSchemaCustom(intl)), [intl]);

  const { onSubmit, getInputProps, reset, values } = useForm({
    validateInputOnBlur: true,
    initialValues: {
      type: get(account, "billingAddress.type", "personal"),
    } as BillingAddress,
    validate: validate,
  });

  const {
    mutate: changeBillingAddress,
    isPending,
  } = useMutation(trpc.changeAccountBillingAddress.mutationOptions());

  if (!stripe || !elements) {
    return <LoadingOverlay />;
  }

  const handleClose = useCallback(() => {
    reset();
    setSubmitError(null);
    onClose();
  }, [reset, onClose, setSubmitError]);

  const handleSubmitBillingAddressChange = useCallback(async (form: typeof values) => {
    const address = elements.getElement("address");
    const taxId = elements.getElement("taxId");
    if (!address) {
      return;
    }
    const { error } = await elements.submit();
    const { value } = await address.getValue();

    let maybeTax: TaxInfo | undefined = {};
    if (taxId) {
      const { value: taxValue } = await taxId.getValue();
      if (taxValue.taxId) {
        maybeTax = taxValue;
      }
    }

    if (error) {
      logger.error({ error }, "Failed to submit address.");
      setSubmitError(error);
      return;
    }
    changeBillingAddress({ ...form, ...maybeTax, ...value }, {
      onSuccess: (updatedAccount) => {
        queryClient.setQueryData(trpc.getCurrentAccount.queryKey(), updatedAccount);
        handleClose();
      },
      onError: (changeBillingError) => {
        setSubmitError(changeBillingError);
      },
    });
  }, [changeBillingAddress, handleClose, queryClient]);

  const isCompany = values.type === "business";

  return (
    <Modal
      size="lg"
      opened={opened}
      onClose={handleClose}
      title={<FormattedMessage id="title.changeBillingAddress" defaultMessage="Change your billing address" />}
    >
      <form onSubmit={onSubmit(handleSubmitBillingAddressChange)}>
        <Stack gap="xs">
          <Text size="sm" pb="xs">
            <FormattedMessage defaultMessage="Your billing address is used to calculate taxes and to generate invoices." id="text.billingAddressDescription" />
          </Text>
          <ErrorMessage error={submitError} />
          <AddressForm
            defaultAddress={account.billingAddress}
            getInputProps={getInputProps}
            isPending={isPending}
            isCompany={isCompany}
          />
        </Stack>
        <Group gap="xs" justify="flex-end" pt="lg">
          <Button
            variant="secondary"
            type="reset"
            loading={isPending}
            onClick={handleClose}
          >
            <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            variant="tertiary"
            type="submit"
            loading={isPending}
          >
            <FormattedMessage id="button.save" defaultMessage="Save" />
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
